import type { ReactNode } from "react";

import { BrandLogo, VeltaCredit } from "@/components/brand-logo";
import { CraneIllustration } from "@/components/crane-illustration";
import { LanguageSwitcher } from "@/components/language-switcher";
import { AUTH_BACKDROP, type BackdropPhoto } from "@/lib/brand-assets";
import { cn } from "@/lib/utils";

interface AuthLayoutProps {
  /** The card: the sign-in form. */
  children: ReactNode;
  /**
   * The photo the card floats over; defaults to `AUTH_BACKDROP` in
   * `@/lib/brand-assets`. Null shows the split screen with the drawn crane.
   */
  backdrop?: BackdropPhoto | null;
}

/**
 * The frame of the screens before sign-in.
 *
 * With a backdrop photo (the default): the photo fills the screen, De Ceuster's
 * white logo sits on a blue block in the top-left corner, the language switcher
 * (NL, EN, FR) on a white pill in the top-right one, and the card floats in
 * the middle with the "Built by Velta" credit on a white label below it.
 *
 * Without one: a split screen, De Ceuster blauw with the white logo and a large
 * line-drawn tower crane on one side, the card on white on the other; on a phone
 * the blue side becomes a short band above the card. The language switcher sits
 * in the top-right corner.
 */
export function AuthLayout({ children, backdrop = AUTH_BACKDROP }: AuthLayoutProps) {
  if (backdrop) {
    return <BackdropLayout photo={backdrop}>{children}</BackdropLayout>;
  }
  return (
    <div className="bg-background relative flex min-h-screen flex-col lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <LanguageSwitcher className="absolute top-4 right-4 z-10 sm:top-6 sm:right-6" />
      <BrandPanel />
      <main className="flex flex-1 flex-col items-center justify-start gap-6 px-4 py-8 sm:px-8 sm:py-12 lg:justify-center">
        {children}
        <VeltaCredit />
      </main>
    </div>
  );
}

function BackdropLayout({ photo, children }: { photo: BackdropPhoto; children: ReactNode }) {
  return (
    <div className="bg-brand relative flex min-h-screen flex-col">
      <img
        src={photo.src}
        srcSet={photo.srcSet}
        sizes="100vw"
        alt=""
        aria-hidden="true"
        className={cn("absolute inset-0 h-full w-full object-cover", photo.focusClass)}
      />
      {/* A light De Ceuster blauw tint: the photo stays a photo, the card stands out. */}
      <div aria-hidden="true" className="bg-brand/20 absolute inset-0" />
      <header className="relative flex items-start justify-between gap-4">
        <div className="bg-brand inline-block px-5 py-4 sm:px-8 sm:py-6">
          <BrandLogo tone="white" className="h-9 sm:h-12" />
        </div>
        <LanguageSwitcher className="mt-4 mr-4 sm:mt-6 sm:mr-8" />
      </header>
      <main className="relative flex flex-1 flex-col items-center justify-center gap-4 px-4 py-8 sm:px-8">
        {/* The card floats: a deep, blue-tinted shadow lifts it off the photo. */}
        <div className="shadow-brand/60 ring-card/50 w-full max-w-md rounded-lg shadow-[0_32px_64px_-12px] ring-1">
          {children}
        </div>
        {/* The link itself is the label: flex-centred, and clickable edge to edge. */}
        <VeltaCredit className="bg-card shadow-brand/40 rounded-full px-4 py-2 leading-none shadow-[0_12px_28px_-8px]" />
      </main>
    </div>
  );
}

function BrandPanel() {
  return (
    <div className="bg-brand text-brand-foreground relative h-36 shrink-0 overflow-hidden sm:h-44 lg:sticky lg:top-0 lg:h-screen">
      {/* On a phone the crane's top peeks in right of the logo; on a desktop it
          stands on the panel's bottom edge, centred. On a narrow phone (below
          about 346 px) 52% would run the jib's tip (18 px into the drawing)
          into the logo, which ends at 188 px (h-10 after px-6), so the drawing
          never starts left of 11.25rem (180 px): the tip stays 10 px clear. */}
      <CraneIllustration className="absolute top-4 left-[max(52%,11.25rem)] w-64 sm:right-[-2.5rem] sm:left-auto sm:w-96 lg:top-auto lg:right-auto lg:bottom-0 lg:left-1/2 lg:h-[80%] lg:w-[min(38rem,100%)] lg:-translate-x-1/2" />
      <div className="relative flex h-full items-center px-6 sm:px-10 lg:items-start lg:p-12 xl:p-16">
        <BrandLogo tone="white" className="h-10 sm:h-12 lg:h-16" />
      </div>
    </div>
  );
}
